"use client";

import { useEffect, useRef, useState } from "react";
import { Award, Clock, Target, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  id: 1,
  icon: Target,
  title: "Access Wider Talent",
  description: "We focus on cultural and values alignment, ensuring that every candidate is the right fit for the organisation and the people you care for and support."
}, {
  id: 2,
  icon: Clock,
  title: "Prevent Accidental Managers",
  description: "We use evidence-based assessment to identify and engage candidates who align with care values and demonstrate strong leadership or leadership potential."
}, {
  id: 3,
  icon: Users,
  title: "Hire With Confidence",
  description: "We focus on identifying and attracting top-tier talent. We leverage industry knowledge to find candidates who are a perfect fit for your organisation."
}, {
  id: 4,
  icon: Award,
  title: "Retain Managers and Teams",
  description: "Our focus on retention strategies helps reduce turnover and increase employee satisfaction, leading to a more stable workforce."
}];
export default function WhyChooseActivate() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: false,
    threshold: 0.3
  });
  return <section ref={sectionRef} className="relative bg-dark-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground-dark mb-4 animate-fade-in-up">
            Why Choose Activate?
          </h2>
          <p className="text-lg text-muted-light max-w-2xl mx-auto animate-fade-in-up-delay-100">Our specialised management recruitment solutions deliver measurable results for your business</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {benefits.map((benefit, i) => <BenefitTile key={benefit.id} benefit={benefit} index={i} isVisible={isVisible} data-index={i} />)}
        </div>
      </div>
    </section>;
}
function BenefitTile({
  benefit,
  index,
  isVisible
}: {
  benefit: typeof benefits[0];
  index: number;
  isVisible: boolean;
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasFlipped, setHasFlipped] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (isVisible && !hasFlipped) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsFlipped(true);
        setHasFlipped(true);
        timeoutRef.current = setTimeout(() => {
          setIsFlipped(false);
        }, 2000);
      }, index * 150);
    } else if (!isVisible && hasFlipped) {
      setHasFlipped(false);
      setIsFlipped(false);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible, hasFlipped, index]);
  const Icon = benefit.icon;
  return <div className="perspective-1000" style={{
    perspective: "1000px"
  }}>
      <Card className="relative h-72 bg-[hsl(210,10%,22%)] border border-muted-dark shadow-medium hover:shadow-[0_0_20px_rgba(215,223,35,0.3)] transition-all duration-[var(--duration-medium)] cursor-pointer group" style={{
      transformStyle: "preserve-3d",
      transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
      transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
    }}>
        {}
        <CardContent className="absolute inset-0 flex flex-col items-center justify-center p-6 backface-hidden" style={{
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden"
      }}>
          <div className="mb-6 p-4 rounded-full bg-primary/10 border border-primary/20 group-hover:bg-primary/20 transition-colors duration-[var(--duration-medium)]">
            <Icon className="w-12 h-12 text-primary stroke-[1.5]" />
          </div>
          <h3 className="text-xl font-display font-semibold text-foreground-dark text-center">
            {benefit.title}
          </h3>
        </CardContent>

        {}
        <CardContent className="absolute inset-0 flex items-center justify-center p-6 bg-primary backface-hidden" style={{
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: "rotateY(180deg)"
      }}>
          <p className="text-center text-base md:text-lg text-foreground-primary font-medium leading-relaxed">
            {benefit.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}